import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout, Spinner } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Textarea } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import { useBackupTime } from "@/features/callback-backup";
import { useCallbackMinutes } from "@/features/callback-minutes";
import { useCallbackTime } from "@/features/callback-picker";
import { CallbackTimes } from "@/features/callback-times";
import { OutcomePicker } from "@/features/outcome-picker";
import { ApiError } from "@/lib/api";
import { backupGapMinutes, backupTimesWhen, backupsKept, callbackNext, callbackWhen, keptBackups } from "@/lib/callbacks";
import { useAuth } from "@/lib/auth";
import { contactName, exitProtected, outcomeEffect, outcomeSaved, stageText } from "@/lib/contacts";
import { keepsCalling, suggestedOutcomes } from "@/lib/dispositions";
import {
  useCallDispositions, useCampaigns, useContactOutcome, useMyOutcomes, useStagesWithCadence,
} from "@/lib/queries";
import { seesCampaigns, atLeast, type Disposition, type LeadCard } from "@/lib/types";

/**
 * Set outcome — the off-call disposition drawer (ADR-044).
 *
 * The same buttons a rep sees after a call, with a one-line effect under each and the next-action
 * sentence printed before Save, so nobody guesses what the click does. Core-api decides all of it
 * (`POST /leads/{id}/disposition`: the stage, the note, a do-not-call, a hold on every list row, a
 * callback at the time picked on the lead's clock — ADR-080, the handover to the campaign the
 * outcome names); the words here say it first. A working outcome on a lead that has left is dimmed
 * with the reason: the exit is protected (ADR-046), and core-api would refuse it anyway.
 *
 * A callback may carry up to four backup times (ACL-559, ADR-183 and ADR-210 in `platform`): if the
 * dialer's call at the time before reaches nobody, it calls again at the next. A contact with a callback still to come is told the new
 * one replaces it, with the old time offered as the backup or as the first.
 *
 * Opened from the board by a drop on an exit column (ADR-078), `forStage` names that stage: the
 * outcomes that put the lead there come first, and the dialog says so — or says that none does.
 */
export function ContactOutcomeDialog({
  open, onOpenChange, contact, forStage = null,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: LeadCard;
  forStage?: string | null;
}) {
  const toast = useToast();
  const { user } = useAuth();
  const outcomes = useCallDispositions(contact.calling_campaign?.id ?? null, open);
  // The names of the campaigns outcomes hand a lead to. A rep cannot list campaigns, and reads
  // "another list" instead (ACL-652).
  const supervisor = seesCampaigns(user?.role);
  const campaigns = useCampaigns({}, { enabled: open && supervisor });
  const handsTo = (outcome: Disposition) =>
    campaigns.data?.find((c) => c.id === outcome.enlist_campaign_id)?.name ?? null;
  // An end stage the calling campaign gives a cadence keeps being called: the words and the icon
  // say so, instead of "leaves this firm's lists" for a lead the dialer is about to ring.
  const withCadence = useStagesWithCadence(contact.calling_campaign?.id ?? null, open);
  const kept = (outcome: Disposition) => keepsCalling(outcome, withCadence);
  const record = useContactOutcome();
  const mine = useMyOutcomes(contact.calling_campaign?.id ?? null, open);
  const callback = useCallbackTime(contact.clock_timezone ?? contact.timezone);
  const backup = useBackupTime(callback.value, [], backupGapMinutes(contact.callback_window_minutes));
  const soon = useCallbackMinutes();
  const [chosen, setChosen] = React.useState<Disposition | null>(null);
  const [note, setNote] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setChosen(null);
    setNote("");
    setError(null);
  }, [open]);

  // A rep is not offered what the platform records by itself; a supervisor may set it (ADR-160).
  const all = outcomes.data ?? [];
  const offered = atLeast(user?.role, "supervisor") ? all : all.filter((o) => o.rep_can_pick !== false);
  const hidden = all.length - offered.length;
  const leadingThere = forStage
    ? offered.filter((o) => outcomeStage(o) === forStage && exitProtected(contact, o) === null)
    : [];
  const leading = leadingThere.length;
  // From a drop on a column, the outcomes that put the contact there; otherwise the person's own most
  // used on the lead's calling campaign.
  const suggested = forStage ? leadingThere.slice(0, 4) : suggestedOutcomes(offered, mine.data ?? []);
  const needsNote = chosen?.requires_note ?? false;
  // Not qualified asks why: one outcome stands for every reason, and the person types it (ACL-574).
  const asksWhy = needsNote && chosen?.outcome_group === "not_qualified";
  const timed = chosen?.kind === "callback";
  const canSave =
    chosen !== null &&
    (!needsNote || note.trim().length > 0) &&
    (!timed || (callback.value.iso !== null && backup.ok));
  const lead = callback.value.leadClock ? callback.value.zone : null;
  const backupTimes = timed ? backup.isos : [];

  const save = () => {
    if (!chosen) return;
    setError(null);
    record.mutate(
      {
        leadId: contact.id,
        disposition: chosen.slug,
        note: note.trim() || null,
        callbackAt: timed ? callback.value.iso : null,
        callbackPurpose: timed ? callback.purposeToSend : null,
        callbackBackupTimes: backupTimes,
      },
      {
        onSuccess: (result) => {
          const when = result.callback_at ? callbackWhen(result.callback_at, lead).there : null;
          const kept = keptBackups(result);
          const backupWhen =
            kept.length > 0 && result.callback_at ? backupTimesWhen(kept, lead, Date.parse(result.callback_at)) : null;
          toast.success(`${chosen.label} recorded`, outcomeSaved(supervisor ? result : { ...result, handed_to_campaign: null }, when, backupWhen));
          // An older core-api takes the callback and drops its backups, or all but the first,
          // without a word: only the answer's echo says what was kept.
          const saved = result.callback_at ? backupsKept(backupTimes, result) : "all";
          if (saved === "none") {
            toast.warning("The backup time was not saved", "This server does not take one yet. The callback is set.");
          } else if (saved === "first") {
            toast.warning("Only the first backup time was saved", "This server keeps one backup time. The callback is set.");
          }
          onOpenChange(false);
        },
        onError: (e) => setError(e instanceof ApiError ? e.message : "Could not record the outcome."),
      },
    );
  };

  const target = chosen ? outcomeStage(chosen) : null;
  const moves = chosen !== null && target !== null && target !== contact.stage;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer" size="md">
        <DialogHeader>
          <DialogTitle>Set outcome</DialogTitle>
          <DialogDescription>
            {contactName(contact) ?? contact.phone} · recorded by you, now, off a call. It moves the stage
            and keeps the note, and a callback is dialed at the time you pick.
          </DialogDescription>
        </DialogHeader>
        <DialogForm
          onSubmit={(e) => {
            e.preventDefault();
            if (canSave) save();
          }}
        >
          <DialogBody className="space-y-4">
            <DiscardGuard changed={chosen !== null || note.trim().length > 0} />
            {forStage && offered.length > 0 && (
              <Callout tone={leading ? "info" : "warning"}>
                {leading
                  ? `${stageText(forStage).label} comes from an outcome. The ones that put a contact there are suggested first.`
                  : supervisor
                    ? `No outcome of this campaign puts a contact in ${stageText(forStage).label}. An admin links one on the Dispositions page.`
                    : `No outcome here puts a contact in ${stageText(forStage).label}. An admin links one on the Dispositions page.`}
              </Callout>
            )}
            {outcomes.isPending ? (
              <div className="flex justify-center py-8">
                <Spinner className="size-4" />
              </div>
            ) : outcomes.error ? (
              <Callout tone="danger">
                {outcomes.error instanceof ApiError ? outcomes.error.message : "Could not load the outcomes."}
              </Callout>
            ) : offered.length === 0 ? (
              <p className="text-sm text-text-muted">
                {supervisor
                  ? "The calling campaign offers no outcomes. An admin picks them on its Dispositions tab."
                  : "There are no outcomes to choose. Tell your supervisor."}
              </p>
            ) : (
              <OutcomePicker
                offered={offered}
                withCadence={withCadence}
                suggested={suggested}
                why={
                  forStage
                    ? `they put the contact in ${stageText(forStage).label}`
                    : supervisor
                      ? "your most used on this campaign this week"
                      : "your most used this week"
                }
                hidden={hidden}
                litId={chosen?.id ?? null}
                disabled={record.isPending}
                unavailable={(outcome) => exitProtected(contact, outcome)}
                describe={(outcome) => outcomeEffect(outcome, handsTo(outcome), kept(outcome))}
                onPick={(outcome) => {
                  setChosen(outcome);
                  if (outcome.kind === "callback" && chosen?.id !== outcome.id) {
                    callback.start(outcome);
                    backup.clear();
                  }
                }}
              />
            )}

            {chosen && timed && (
              <CallbackTimes
                leadId={contact.id}
                outcome={chosen.slug}
                callback={callback}
                backup={backup}
                soon={soon}
                disabled={record.isPending}
                withPurpose
                windowMinutes={contact.callback_window_minutes}
                open={contact.callback}
              />
            )}

            {chosen && (
              <Field
                label={
                  asksWhy
                    ? "Why is this lead not qualified? · required"
                    : needsNote
                      ? `Note · required for ${chosen.label}`
                      : "Note · kept on the record"
                }
                htmlFor="outcome-note"
                required={needsNote}
              >
                <Textarea
                  rows={3}
                  maxLength={4000}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="font-sans text-sm"
                  placeholder={
                    asksWhy
                      ? "Has an attorney, at fault, past the statute of limitations…"
                      : "What was said, and what happens next"
                  }
                />
              </Field>
            )}

            {chosen && (
              <p className="text-xs text-text-muted" aria-live="polite">
                <span className="font-medium text-text">Next:</span>{" "}
                {timed && callback.value.instant !== null
                  ? callbackNext(callback.value.instant, lead, backup.isos.length > 0 ? backup.instants : [])
                  : outcomeEffect(chosen, handsTo(chosen), kept(chosen))}
                .{" "}
                {moves ? (
                  <>
                    Stage {stageText(contact.stage).label} → {stageText(target).label}, since now.
                  </>
                ) : (
                  <>The stage stays {stageText(contact.stage).label}.</>
                )}
              </p>
            )}

            {error && <Callout tone="danger">{error}</Callout>}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={record.isPending} disabled={!canSave}>
              Save outcome
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

/** The stage an outcome puts a lead in: a compliance outcome is always Do not call (ADR-046). */
function outcomeStage(outcome: Disposition): string | null {
  return outcome.category === "compliance" ? "dnc" : outcome.stage ?? null;
}
